/**
 * What a "Per-station PDF" request covers.
 *
 * The dock's brush decides the range (owner decision: the current selection if
 * there is one, otherwise the whole corridor) and this module turns that into
 * the two numbers the mutation carries plus the page count the confirm copy
 * quotes.
 *
 * The count must be the number of pages the SERVER will print, not the number
 * of bins in the range: `preprocess.ts::pageableBins` gives a page only to a
 * bin that measured something, and a dialog that promised 251 pages for a
 * 249-page document would be wrong on the one number the user reads twice.
 * The station WINDOW, on the other hand, stays around the whole range — the
 * skipped bins belong on the appendix page, and they only get there if they
 * fall inside the window preprocess narrows to.
 */
import type { StationBin, StationVolumesLayerMeta } from './types';

/** Owner cap on one request. Mirrors `MAX_STATION_PAGES` in
 *  `server/background/src/functions/preprocess.ts`; the server enforces it, this
 *  is what turns a rejected batch into a sentence before anything is queued. */
export const MAX_STATION_PAGES = 400;

export interface PerStationPdfScope {
  /** The bins that will get a page. */
  bins: StationBin[];
  /** Internal stations, or null for "the whole analysis". */
  stationFrom: number | null;
  stationTo: number | null;
  /**
   * The same window in DISPLAY stations, or null for the whole analysis. The
   * label the dialog shows has to name the window that was sent — including
   * the blank bins at its ends, which get no page but do get an appendix line
   * and are inside the range the cover note states.
   */
  displayFrom: number | null;
  displayTo: number | null;
  pageCount: number;
  /**
   * Bins inside the WINDOW, blank ones included — which is what the render
   * job draws, and therefore what the render estimate must be computed from.
   * `scripts/render_per_surface.py::station_features` keeps every bin polygon
   * wholly inside `[stationFrom, stationTo]` and never consults `cellCount`,
   * while `station_bins.py::station_bin_features` writes a polygon for every
   * bin. So a brush with blank bins in it renders more frames than it prints
   * pages, and quoting `pageCount` would under-promise.
   */
  renderCount: number;
  overCap: boolean;
}

export function perStationPdfScope(
  bins: readonly StationBin[],
  selection: { fromBin: number; toBin: number } | null,
): PerStationPdfScope {
  const lo = selection ? Math.max(0, Math.min(selection.fromBin, selection.toBin)) : 0;
  const hi = selection ? Math.min(bins.length - 1, Math.max(selection.fromBin, selection.toBin)) : bins.length - 1;
  /**
   * A brush left behind by a longer run can sit entirely past the end of the
   * bin array, which clamps to an empty window. An empty document is not what
   * the user asked for and a range of "nothing" is not a range, so the request
   * widens to the whole corridor — the same fallback the summary PDF item
   * makes, and the one the dock test pins.
   */
  const windowed = selection !== null && bins.length > 0 && lo <= hi;
  const range = windowed ? bins.slice(lo, hi + 1) : bins;
  const pages = range.filter((b) => b.cellCount > 0);
  const first = windowed ? range[0] : undefined;
  const last = windowed ? range[range.length - 1] : undefined;
  return {
    bins: pages,
    stationFrom: first ? first.staStartInternalM : null,
    stationTo: last ? last.staEndInternalM : null,
    displayFrom: first ? first.staStartDisplayM : null,
    displayTo: last ? last.staEndDisplayM : null,
    pageCount: pages.length,
    renderCount: range.length,
    overCap: pages.length > MAX_STATION_PAGES,
  };
}

/**
 * Can this run produce per-station point-cloud renders (spec §13.7)?
 *
 * Two conditions, both read off the layer's own metadata: the analysis was a
 * DEVIATION run (a surface difference has no coloured LAZ to render — a
 * dz-raster renderer is a later addendum), and it registered a
 * `station_bins.geojson` byproduct (runs made before that addendum have no bin
 * polygons). When this is false the checkbox is not rendered at all rather
 * than rendered-and-disabled: there is nothing the reader could do to make it
 * true, so an inert control would only pose a question with no answer.
 */
export const stationRendersAvailable = (meta: StationVolumesLayerMeta | null | undefined): boolean =>
  meta?.kind === 'deviation' && !!meta.byproducts?.bins;

/**
 * Fixed cost of the render stage, in seconds: pulling the image and starting
 * the Fargate task, before the first station is drawn. Quoted even for a
 * three-page request, because it is paid even for a three-page request.
 */
export const RENDER_LOAD_SECONDS = 20;

/** Marginal cost per station page. Spec §13.7 measures 0.5–1 s on the render
 *  task; 0.8 is the honest middle. */
export const RENDER_SECONDS_PER_BIN = 0.8;

/**
 * Seconds to quote in the checkbox label: `load + n × perBin` (ruling F8).
 * The two constants are exported beside it so the estimate the dialog prints
 * and the estimate the tests pin cannot drift apart.
 */
export const estimateRenderSeconds = (pageCount: number): number =>
  Math.ceil(RENDER_LOAD_SECONDS + pageCount * RENDER_SECONDS_PER_BIN);

/**
 * The estimate as a reader feels it. Raw seconds stop being a quantity anyone
 * can judge somewhere around two minutes — at the 400-page cap the honest
 * figure is 340 s, and "≈ 6 min" is the number a surveyor actually decides on.
 *
 * The units are the SI symbols `s` and `min`, which are the same in all five
 * of the app's languages; they are deliberately NOT locale keys, because a
 * `report.*` key whose German value is also "min" would fail the "really
 * translated" parity check in `stationVolumesI18n.test.ts` for being identical
 * to English. The surrounding sentence is translated; only the symbol is not.
 */
export const formatDurationShort = (seconds: number): string =>
  seconds < 90 ? `≈ ${Math.round(seconds)} s` : `≈ ${Math.ceil(seconds / 60)} min`;

/**
 * Why a layer stack cannot produce a per-station PDF right now, or `null` when
 * it can.
 *
 * The stack's per-station PDF is a real document (spec §7's stacked
 * cross-section, printed one page per station), and `preprocess.ts`'s
 * STATION_PAGES arm resolves it from the stack's OWN artifacts: the bins out
 * of `stack.json`, the drawing out of the 2N-plane `station_sections.f32`, the
 * marking out of `metadata.layerStack.levels[]`. But not every stack has all
 * of them, and a button that starts a batch nobody can finish is worse than no
 * button — the user waits minutes for a `toast.error`.
 *
 * So this is the CLIENT half of a two-halves gate. Each reason here has a
 * refusal on the server that fires on exactly the same condition, in the same
 * order:
 *
 * | reason        | `preprocess.ts` STATION_PAGES / stack branch          |
 * |---------------|-------------------------------------------------------|
 * | `noStations`  | "was analysed without an alignment"                   |
 * | `pendingLayer`| "has not been built yet"                              |
 * | `noSections`  | "no readable station_sections.f32 byproduct"          |
 * | `loading`     | (client only — the document has not been fetched yet) |
 *
 * `loading` is the one with no server twin: it is not a refusal, it is "ask
 * again in a moment", and the item is disabled rather than explained.
 *
 * Returning a REASON rather than a boolean is deliberate: the disabled item
 * carries the sentence, and a reader who cannot print needs to know which of
 * the four things to change.
 */
export type PerStationPdfStackBlock = 'loading' | 'noStations' | 'pendingLayer' | 'noSections';

export const perStationPdfStackBlock = (
  meta: { stationParams: unknown; byproducts?: { sections?: string | null } | null } | null,
  /** `stack.json`, once it has been fetched. `null` while it is loading. */
  doc: { layers?: { state?: string; stations?: readonly unknown[] }[] } | null,
  layerIndex: number,
): PerStationPdfStackBlock | null => {
  if (!meta) return null;
  // No alignment on the run means no station breakdown at all: there are no
  // bins to page and no station lines to cut sections at.
  if (!meta.stationParams) return 'noStations';
  // The 2N-plane sections file IS the page (D62). A stack always writes one,
  // so this only fires when its registration failed or the row is gone.
  if (!meta.byproducts?.sections) return 'noSections';
  if (!doc) return 'loading';
  const layer = doc.layers?.[layerIndex];
  // A PENDING layer has no `stations` key at all — the level above it has not
  // been surveyed. Nothing was measured, so nothing can be printed; the reader
  // picks a different chip.
  if (!layer || layer.state === 'PENDING' || !layer.stations || layer.stations.length === 0) return 'pendingLayer';
  return null;
};
